import { useState, useEffect } from 'react';
import { Plus, MapPin, EyeOff } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { useLocations } from './hooks';

import { SlidePanel } from '../../components/common/SlidePanel';
import { AddressAutocomplete } from '../../components/common/AddressAutocomplete';
import type { AddressResult } from '../../components/common/AddressAutocomplete';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import type { Location } from '../../types';

const LOCATION_TYPES: Record<string, { label: string; bg: string; fg: string }> = {
  rental_unit:  { label: 'Rental Unit',  bg: '#EDE9FE',        fg: '#7C3AED' },
  listing:      { label: 'Listing',      bg: palette.blueWash,  fg: palette.blue },
  job_site:     { label: 'Job Site',     bg: palette.amberWash, fg: palette.amber },
  shop:         { label: 'Shop',         bg: palette.greenWash, fg: palette.green },
  office:       { label: 'Office',       bg: palette.accentWash, fg: palette.accentDeep },
  client_home:  { label: 'Client Home',  bg: '#FFF7ED',        fg: '#CA5010' },
  building:     { label: 'Building',     bg: palette.g100,      fg: palette.g600 },
  property:     { label: 'Property',     bg: palette.greenWash, fg: palette.green },
  venue:        { label: 'Venue',        bg: '#CCFBF1',         fg: '#0F766E' },
  other:        { label: 'Other',        bg: palette.g100,      fg: palette.g500 },
};

const STATUS_CFG: Record<string, { label: string; bg: string; fg: string }> = {
  active:      { label: 'Active',      bg: palette.greenWash, fg: palette.green },
  inactive:    { label: 'Inactive',    bg: palette.g100,      fg: palette.g500 },
  maintenance: { label: 'Maintenance', bg: palette.amberWash, fg: palette.amber },
  turnover:    { label: 'Turnover',    bg: palette.redWash,   fg: palette.red },
};

const ALL_STATUSES = Object.keys(STATUS_CFG);

const TH: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: admin.caption, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${admin.border}`, whiteSpace: 'nowrap' };
const TD: React.CSSProperties = { fontSize: 13, color: palette.g700, padding: '10px 12px', borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap' };
const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };

export function LocationsPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const { features, isLensActive, industryName } = useIndustryLens();
  const { locations, clients, loading, error, summary, filters, setFilters, createLocation, updateLocation, deactivate } = useLocations(business?.id);
  const [editing, setEditing] = useState<Location | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => { document.title = 'CrewBit · Locations'; }, []);

  if (isLensActive && !features.showLocations) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Locations</div>
        <div style={{ padding: '60px 20px', textAlign: 'center', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <EyeOff size={24} color={palette.g400} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: palette.g700, marginBottom: 6 }}>Locations is not typically used by {industryName} businesses</div>
          <div style={{ fontSize: 13.5, color: palette.g500 }}>Switch your industry lens to see this module, or exit the lens to return to your view.</div>
        </div>
      </div>
    );
  }

  if (loading) return <div><Skeleton width={100} height={24} style={{ marginBottom: 24 }} /><div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><TableSkeleton rows={5} columns={5} /></div></div>;
  if (error) return <div><div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Locations</div><div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div></div>;

  const cMap = new Map(clients.map(c => [c.id, c.name]));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Locations</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Sites, offices, and properties</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8, border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={15} /> Add Location</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Active locations" value={String(summary.activeCount)} />
        {summary.byType.map(bt => (
          <MetricCard key={bt.type} label={LOCATION_TYPES[bt.type]?.label ?? bt.type} value={String(bt.count)} />
        ))}
      </div>

      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="Search locations..."
            style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 220 }} />
          <select value={filters.type ?? ''} onChange={e => setFilters({ ...filters, type: e.target.value || null })}
            style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
            <option value="">All Types</option>
            <optgroup label="Rentals">
              {['rental_unit', 'listing'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
            <optgroup label="Work sites">
              {['job_site', 'shop', 'office', 'building'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
            <optgroup label="Properties">
              {['client_home', 'property', 'venue'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
            <optgroup label="Other">
              {['other'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
          </select>
          <select value={filters.status ?? ''} onChange={e => setFilters({ ...filters, status: e.target.value || null })}
            style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
            <option value="">All Statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
          </select>
        </div>

        {/* Table */}
        {locations.length === 0 ? (
          <EmptyState icon={<MapPin size={22} />} title="No locations yet" subtitle="Add a location to associate with jobs and bookings." actionLabel="Add Location" onAction={() => setShowCreate(true)} />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={TH}>Name</th><th style={TH}>Address</th><th style={TH}>Type</th>
              <th style={TH}>Status</th><th style={TH}>Client</th>
            </tr></thead>
            <tbody>
              {locations.map(l => {
                const lt = LOCATION_TYPES[l.type] ?? LOCATION_TYPES.other;
                const ls = STATUS_CFG[l.status] ?? STATUS_CFG.active;
                return (
                  <tr key={l.id} onClick={() => setEditing(l)} style={{ cursor: 'pointer', transition: `background ${transition.fast}` }}
                    onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{l.name}</td>
                    <td style={TD}>{[l.address, l.unit, l.city].filter(Boolean).join(', ') || '—'}</td>
                    <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: lt.bg, color: lt.fg }}>{lt.label}</span></td>
                    <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: ls.bg, color: ls.fg }}>{ls.label}</span></td>
                    <td style={TD}>{l.client_id ? cMap.get(l.client_id) ?? '—' : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Panel */}
      <LocationPanel isOpen={showCreate || !!editing} location={editing} clients={clients}
        onClose={() => { setShowCreate(false); setEditing(null); }}
        onSave={async (data) => {
          if (!user) return;
          if (editing) {
            const ok = await updateLocation(editing.id, data, user.id);
            if (ok) { setToast({ msg: 'Location updated', type: 'success' }); setEditing(null); }
          } else {
            const id = await createLocation(data, user.id);
            if (id) { setToast({ msg: 'Location created', type: 'success' }); setShowCreate(false); }
          }
        }}
        onDeactivate={editing ? async () => {
          if (!user || !editing) return;
          const ok = await deactivate(editing.id, user.id);
          if (ok) { setToast({ msg: 'Location deactivated', type: 'success' }); setEditing(null); }
        } : undefined}
      />

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

// ── Side Panel ──
interface PanelProps {
  isOpen: boolean;
  location: Location | null;
  clients: { id: string; name: string }[];
  onClose: () => void;
  onSave: (data: Partial<Location>) => void;
  onDeactivate?: () => void;
}

function LocationPanel({ isOpen, location, clients, onClose, onSave, onDeactivate }: PanelProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState('job_site');
  const [status, setStatus] = useState('active');
  const [address, setAddress] = useState('');
  const [unit, setUnit] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [clientId, setClientId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && location) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- prop-sync form pattern; key-remount refactor out of scope for §1.7
      setName(location.name); setType(location.type); setStatus(location.status);
      setAddress(location.address ?? ''); setUnit(location.unit ?? ''); setCity(location.city ?? '');
      setProvince(location.province ?? ''); setPostalCode(location.postal_code ?? '');
      setLatitude(location.latitude ?? null); setLongitude(location.longitude ?? null);
      setClientId(location.client_id ?? ''); setNotes(location.notes ?? '');
    } else if (isOpen) {
      setName(''); setType('job_site'); setStatus('active'); setAddress(''); setUnit('');
      setCity(''); setProvince(''); setPostalCode(''); setLatitude(null); setLongitude(null); setClientId(''); setNotes('');
    }
    setErr(null);
  }, [isOpen, location]);

  const handleSave = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr(null);
    await onSave({ name: name.trim(), type, status, address: address || null, unit: unit || null, city: city || null, province: province || null, postal_code: postalCode || null, latitude, longitude, client_id: clientId || null, notes: notes || null });
    setSaving(false);
  };

  const F: React.CSSProperties = { marginBottom: 14 };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title={location ? 'Edit Location' : 'New Location'} subtitle={location ? location.name : 'Add a location'}>
      {err && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{err}</div>}

      <div style={F}><label style={LABEL}>Name *</label><input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 42 Oak St — Unit 1" /></div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Type</label>
          <select style={INPUT} value={type} onChange={e => setType(e.target.value)}>
            <optgroup label="Rentals">
              {['rental_unit', 'listing'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
            <optgroup label="Work sites">
              {['job_site', 'shop', 'office', 'building'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
            <optgroup label="Properties">
              {['client_home', 'property', 'venue'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
            <optgroup label="Other">
              {['other'].filter(t => LOCATION_TYPES[t]).map(t => <option key={t} value={t}>{LOCATION_TYPES[t].label}</option>)}
            </optgroup>
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>Status</label>
          <select style={INPUT} value={status} onChange={e => setStatus(e.target.value)}>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
          </select>
        </div>
      </div>
      <div style={F}>
        <label style={LABEL}>Address</label>
        <AddressAutocomplete
          initialValue={address}
          onSelect={(r: AddressResult) => {
            setAddress(r.address);
            setCity(r.city);
            setProvince(r.province);
            setPostalCode(r.postalCode);
            setLatitude(r.latitude);
            setLongitude(r.longitude);
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Unit</label><input style={INPUT} value={unit} onChange={e => setUnit(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>City</label><input style={INPUT} value={city} onChange={e => setCity(e.target.value)} /></div>
      </div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Province</label><input style={INPUT} value={province} onChange={e => setProvince(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Postal code</label><input style={INPUT} value={postalCode} onChange={e => setPostalCode(e.target.value)} /></div>
      </div>
      <div style={F}><label style={LABEL}>Client (optional)</label>
        <select style={INPUT} value={clientId} onChange={e => setClientId(e.target.value)}>
          <option value="">None</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div style={F}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} /></div>

      {/* Lat/lng read-only if available */}
      {location && (location.latitude || location.longitude) && (
        <div style={{ fontSize: 12, color: palette.g400, marginBottom: 14, display: 'flex', gap: 12 }}>
          <span>Lat: {location.latitude?.toFixed(6) ?? '—'}</span>
          <span>Lng: {location.longitude?.toFixed(6) ?? '—'}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        {location && onDeactivate && location.status !== 'inactive' && (
          <button onClick={onDeactivate} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${palette.red}`, background: palette.redWash, fontSize: 13, fontWeight: 500, color: palette.red, cursor: 'pointer', fontFamily: 'inherit', marginRight: 'auto' }}>Deactivate</button>
        )}
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Saving...' : location ? 'Update' : 'Create'}</button>
      </div>
    </SlidePanel>
  );
}
